/**
 * Shopinfo — PONTE PDP do frete: liga a fonte de rates por produto
 * (shopinfo-shipping-rates-api.js) ao calculador global e pinta o resumo
 * inline da PDP. Nasceu no split da TASK-053.
 *
 * Direção da dependência, numa direção só: este arquivo conhece
 * `Shopinfo.shippingRates` (a fonte) e `Shopinfo.calculator` (o global);
 * nenhum dos dois conhece este. Vive aqui, e não no global, porque só a PDP
 * paga por ele — home e coleção carregam o calculador sozinho e não têm nem
 * este código para falhar.
 *
 * Toda copy vem de locale pelos data-msg-* do container; nada de texto visível
 * escrito aqui.
 */
(function () {
  const REG = (window.Shopinfo || {}).calculator;
  const SRC = (window.Shopinfo || {}).shippingRates;
  // Espelha ThemeEvents.variantUpdate (assets/events.js). Literal porque este
  // arquivo é script clássico, sem import — `variant:selected` NÃO serve:
  // dispara antes de a variante nova estar resolvida.
  const VARIANT_EVENT = 'variant:update';
  // Debounce da recotação por troca de variante: uma requisição por rajada.
  const DEBOUNCE = 400;
  // Teto do inline: o resto das opções mora no modal (arbitragem ux×ui).
  const MAX_INLINE = 2;

  const TRUCK =
    '<svg class="shopinfo-shipping-inline__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M1 3h15v13H1z"/><path d="M16 8h4l3 4v5h-7V8z"/>' +
    '<circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';

  const ALERT_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"' +
    ' aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>' +
    '<line x1="12" y1="16" x2="12.01" y2="16"/></svg>';

  let node = null;
  // Geração: só a cotação mais recente pode pintar o inline. O abort sozinho
  // não bastaria — o dedupe da fonte devolve a MESMA promise para alvos iguais,
  // e abortá-la mataria a cotação em curso para todo mundo.
  let gen = 0;
  let abort = null;
  let timer = null;
  let shown = false;
  // Lista COMPLETA da cotação corrente: o inline mostra 2, o modal mostra tudo.
  let full = [];

  const esc = (v) => REG.esc(v);
  // TASK-179: so shopinfo-analytics.js ouve.
  const ev = (n, d) => document.dispatchEvent(new CustomEvent('shopinfo:' + n, { detail: d }));

  // O morph do product-form pode trocar o nó: revalida antes de confiar nele.
  function target() {
    if (!node || !node.isConnected) node = document.getElementById('shopinfo-shipping-inline');
    return node;
  }

  function cta() {
    return document.querySelector('[data-shipping-quote-pdp]');
  }

  // Sem fonte, sem token no metafield ou sem alvo não há cotação por produto:
  // quem chamou cai no cart-based do global. Degradação, não erro.
  function ready() {
    const d = target()?.dataset;
    return !!(SRC && d && d.apiUrl && d.apiToken && d.variantId);
  }

  /* ---------------------------------------------------------------------
   * Render. `paint` é o ÚNICO ponto de escrita no DOM: garante que CTA e
   * inline sejam mutuamente exclusivos (estado vazio devolve o CTA, qualquer
   * outro o esconde) e que a entrada animada valha para todo estado.
   * ------------------------------------------------------------------- */
  function paint(html, state) {
    const n = target();
    if (!n) return null;
    shown = state === 'result';
    if (state !== 'result') full = [];
    n.innerHTML = html;
    n.hidden = !state;
    n.dataset.state = state;
    n.classList.remove('is-ready');
    const c = cta();
    if (c) {
      c.hidden = !!state && !c.closest('[data-shipping-line]');
      // O CTA é o gatilho de revelação do form (Correção 1): o anúncio segue o
      // estado real do container que ele controla.
      c.setAttribute('aria-expanded', state === 'form' ? 'true' : 'false');
    }
    // A classe só entra no frame seguinte para que a transição tenha de onde
    // partir. Se o rAF nunca rodar, o conteúdo aparece sem animação — nunca
    // invisível (por isso a animação vive na classe, não no estado base).
    if (state) requestAnimationFrame(() => n.classList.add('is-ready'));
    return n;
  }

  // Silêncio honesto: sem cotação válida o inline some e o CTA volta. Nunca
  // exibir valor cacheado como se fosse o atual.
  function clear() {
    paint('', '');
    line();
  }

  // Dia da semana por extenso (ajuste 1 da TASK-176, padrao ML): "21/10" ->
  // "quarta-feira, 21/10". A DATA continua decidida so por `untilDayMonth` do
  // calculador; aqui so se nomeia o dia. Ano: o corrente, ou o seguinte quando
  // o dd/mm ja ficou para tras (virada de ano).
  const WEEKDAY = new Intl.DateTimeFormat('pt-BR', { weekday: 'long' });
  function weekday(dm) {
    const x = /^(\d\d)\/(\d\d)$/.exec(dm);
    if (!x) return dm;
    const now = new Date();
    const dt = new Date(now.getFullYear(), x[2] - 1, +x[1]);
    if (dt < now - 2678400000) dt.setFullYear(dt.getFullYear() + 1);
    return WEEKDAY.format(dt) + ', ' + dm;
  }

  // Linha (TASK-175): sem rate volta ao E1, link guardado mantem o listener.
  // Toda saida tira o `is-loading` (TASK-176): falha nunca deixa fantasma, e
  // o E1 ja esta nas metades desde o HTML, so invisivel. Metade 1 = promessa
  // (verde inteira quando gratis, `__part--free`); metade 2 = "comprando
  // hoje" (com preco, se pago) + CEP + alterar.
  function line(r, calc) {
    const l = document.querySelector('[data-shipping-line]');
    const p = l && l.querySelectorAll('.shopinfo-shipping-line__part');
    if (!p || p.length < 2) return;
    l.classList.remove('is-loading', 'is-ready');
    const b = p[1].firstElementChild;
    const e = (l._e1 = l._e1 || [p[0].innerHTML, b, b.textContent]);
    const free = 'shopinfo-shipping-line__part--free';
    if (!r) {
      p[0].innerHTML = e[0];
      p[0].classList.add(free);
      e[1].textContent = e[2];
      p[1].replaceChildren(e[1]);
      return geo(l, p);
    }
    const d = l.dataset;
    const F = window.Shopinfo.shippingFormat;
    const v = parseFloat(r.price);
    const m = F.money(v);
    const w = F.untilDayMonth(r.delivery_days_max ?? r.delivery_days_min);
    p[0].textContent = w ? F.fill(v ? d.msgPaidUntil : d.msgFreeUntil, [weekday(w)]) : v ? F.fill(d.msgPaidService, [m, r.name]) : d.msgFreeCep;
    p[0].classList.toggle(free, !v);
    d.st = w ? (v ? 'E3' : 'E2') : 'E5';
    p[1].innerHTML =
      (w ? esc(v ? F.fill(d.msgPaidToday, [m]) : d.msgFreeToday) + ' · ' : '') +
      esc(F.fill(d.msgCep, [calc.maskCep(calc.savedCep)])) +
      ' · <button type="button" class="shopinfo-shipping-line__link">' + esc(d.msgChange) + '</button>';
    p[1].lastChild.addEventListener('click', () => openForm(calc, true));
    requestAnimationFrame(() => l.classList.add('is-ready'));
  }

  // Geo-IP (TASK-177). Roda a cada volta ao E1: sem CEP salvo (cart ou
  // localStorage) e com a UF do IP na lista de frete gratis, troca SO o texto
  // das metades (mesmos nos, mesma altura). Cache de sessao positivo e
  // negativo; uma tentativa por sessao, so apos o `load` e em idle. O IP nunca
  // vira CEP, nunca cota; qualquer falha deixa o generico em silencio.
  const GEO_KEY = 'shopinfo_geo';
  let geoBusy = false;

  function geo(l, p) {
    const d = l.dataset;
    const calc = REG && REG.instance;
    if (!d.geoUrl || !calc || calc.savedCep || geoBusy) return;
    let c = 0;
    try {
      c = JSON.parse(sessionStorage.getItem(GEO_KEY));
    } catch (x) {}
    if (c && c.uf) {
      if (!d.freeUfs.replace(/\s/g, '').split(',').includes(c.uf)) return;
      p[0].textContent = window.Shopinfo.shippingFormat.fill(d.msgFreeUf, [c.uf]);
      p[1].firstElementChild.textContent = d.msgNotYourState;
      return;
    }
    // `null` = nunca perguntou nesta sessao; storage bloqueado (0) = nao pergunta.
    if (c !== null || document.readyState !== 'complete') return;
    geoBusy = true;
    (window.requestIdleCallback || setTimeout)(() => {
      const a = new AbortController();
      setTimeout(() => a.abort(), 2500);
      fetch(d.geoUrl, { signal: a.signal, credentials: 'omit' })
        .then((r) => r.json())
        .then((j) => (j && j.country === 'BR' && /^[A-Z]{2}$/.test(j.uf) ? j.uf : null), () => null)
        .then((uf) => {
          try {
            sessionStorage.setItem(GEO_KEY, JSON.stringify({ uf: uf }));
          } catch (x) {}
          geoBusy = false;
          // Linha levada pelo morph: o cache reaplica na proxima volta ao E1.
          if (uf && l.isConnected) geo(l, p);
          ev('geo_hint', { uf: uf || '' });
        });
    });
  }

  // "Calculando" com a mesma anatomia do resultado: a altura do bloco não muda
  // quando os rates chegam (CLS).
  function loading() {
    // Mesma guarda de `paint`: o morph do product-form pode ter levado o
    // container embora (troca para variante esgotada) com cotação em voo.
    const t = target();
    if (!t) return;
    const d = t.dataset;
    const ghost =
      '<div class="shopinfo-shipping-inline__rate shopinfo-shipping-inline__rate--ghost" aria-hidden="true">' +
      '<span class="shopinfo-shipping-inline__bar shopinfo-shipping-inline__bar--icon"></span>' +
      '<div class="shopinfo-shipping-inline__lines">' +
      '<span class="shopinfo-shipping-inline__bar"></span>' +
      '<span class="shopinfo-shipping-inline__bar shopinfo-shipping-inline__bar--sm"></span>' +
      '</div></div>';
    paint(
      ghost + ghost +
        '<div class="shopinfo-shipping-inline__footer">' +
        '<span class="shopinfo-shipping-inline__status">' + esc(d.msgCalculating) + '</span></div>',
      'loading'
    );
  }

  function days(rate) {
    const n = parseInt(rate.delivery_days_max, 10);
    return isNaN(n) ? 99 : n;
  }

  // Mais barata sempre em primeiro. A mais rápida só ocupa a segunda linha se
  // for outra opção E realmente chegar antes — repetir prazo igual por preço
  // maior não ajuda ninguém a decidir.
  function pick(rates) {
    const cheap = [...rates].sort((a, b) => parseFloat(a.price) - parseFloat(b.price))[0];
    const fast = [...rates].sort((a, b) => days(a) - days(b))[0];
    const two = fast && fast !== cheap && days(fast) < days(cheap);
    return (two ? [cheap, fast] : [cheap]).slice(0, MAX_INLINE);
  }

  function rateRow(calc, rate, d) {
    const price = parseFloat(rate.price);
    // "Grátis" só quando a cotação corrente voltou zero — nunca por promessa,
    // e sem exibir "R$ 0,00" junto.
    const priceHtml =
      price === 0
        ? '<span class="shopinfo-shipping-inline__free">' + esc(d.msgFree) + '</span>'
        : esc(window.Shopinfo.shippingFormat.money(price));
    // Sem prazo do carrier a linha 1 exibe o serviço: prazo inventado é proibido.
    const promise = calc.promiseFor(rate);
    const detail = promise ? esc(rate.name) + ' &middot; ' + priceHtml : priceHtml;
    return (
      '<div class="shopinfo-shipping-inline__rate">' + TRUCK +
      '<div class="shopinfo-shipping-inline__lines">' +
      '<span class="shopinfo-shipping-inline__delivery">' + esc(promise || rate.name) + '</span>' +
      '<span class="shopinfo-shipping-inline__detail">' + detail + '</span>' +
      '</div></div>'
    );
  }

  // Rodapé "CEP … · alterar" (+ link opcional). Acompanha TODO estado que
  // ocupa o lugar do CTA — inclusive os de erro: sem ele, "sem cobertura"
  // seria beco sem saída, com o CTA escondido e nenhum caminho de volta ao CEP.
  function footer(calc, d, more) {
    const cep = window.Shopinfo.shippingFormat.fill(d.msgCep, [calc.maskCep(calc.savedCep)]);
    return (
      '<div class="shopinfo-shipping-inline__footer">' +
      '<span class="shopinfo-shipping-inline__cep">' + esc(cep) + '</span>' +
      '<button type="button" class="shopinfo-shipping-inline__change">' + esc(d.msgChange) + '</button>' +
      (more || '') +
      '</div>'
    );
  }

  // "alterar" reabre o FORM INLINE pré-preenchido — nunca o modal (Correção 1).
  // Trocar o CEP é o mesmo gesto que informá-lo; dois caminhos para a mesma
  // pergunta seria incoerência. Nunca recota direto: quem altera quer decidir.
  function bindFooter(n, calc) {
    n?.querySelector('.shopinfo-shipping-inline__change')
      ?.addEventListener('click', () => openForm(calc, true));
  }

  /* ---------------------------------------------------------------------
   * Estado `form`: a entrada de CEP acontece ONDE a resposta vai aparecer,
   * dentro da reserva de altura do container — nenhum modal abre para pedir
   * CEP na PDP. O modal continua global (header) e vira só "ver todas as
   * opções". A marcação vive num <template> do Liquid: copy de locale sem
   * escape manual e sem gastar o teto de bytes deste arquivo.
   * ------------------------------------------------------------------- */
  function openForm(calc, prefill) {
    const tpl = document.getElementById('shopinfo-shipping-inline-form');
    const n = tpl && paint('', 'form');
    if (!n) return;
    n.appendChild(tpl.content.cloneNode(true));
    const input = n.querySelector('.shopinfo-shipping-inline__input');
    if (!input) return;
    const err = n.querySelector('.shopinfo-shipping-inline__cep-error');
    // `prefill` distingue os dois gatilhos: CTA sem CEP salvo (campo vazio) e
    // "alterar" (CEP corrente, selecionado para ser substituído de uma vez).
    if (prefill && calc.savedCep) input.value = calc.maskCep(calc.savedCep);

    // Máscara e noção de CEP válido são as do calculador global: um só lugar
    // decide o que é um CEP, aqui e no modal.
    input.addEventListener('input', (e) => {
      e.target.value = calc.maskCep(e.target.value);
    });

    const submit = () => {
      const raw = input.value.replace(/\D/g, '');
      // Validação SÓ no submit: acusar durante a digitação marcaria de errado
      // todo CEP pela metade. CEP inválido não cota.
      if (raw.length !== 8) {
        input.classList.add('is-invalid');
        if (err) err.textContent = n.dataset.msgCepInvalid;
        ev('shipping_quote_result', { status: 'cep_invalid' });
        input.focus();
        return;
      }
      calc.saveCep(raw);
      quoteInline(calc, raw);
    };

    n.querySelector('.shopinfo-shipping-inline__ok')?.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => {
      // Enter NUNCA pode borbulhar: este container vive dentro do form do
      // produto e a submissão implícita adicionaria ao carrinho.
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
      // Esc é saída sem beco: recolhe o form, devolve o CTA e o foco a ele.
      if (e.key === 'Escape') {
        e.preventDefault();
        clear();
        cta()?.focus();
      }
    });

    input.focus();
    if (prefill) input.select();
  }

  function render(calc, rates) {
    // Idem `loading`: continuação assíncrona não pode presumir o container.
    const t = target();
    if (!t) return;
    const d = t.dataset;
    const picked = pick(rates);
    // O link para a lista completa só existe quando há lista completa a ver.
    const more =
      rates.length > picked.length
        ? '<button type="button" class="shopinfo-shipping-inline__all">' + esc(d.msgAllOptions) + '</button>'
        : '';
    const n = paint(picked.map((r) => rateRow(calc, r, d)).join('') + footer(calc, d, more), 'result');
    full = rates;
    line(picked[0], calc);
    bindFooter(n, calc);
    // "ver todas" reusa a cotação que já está na mão: abrir o modal não gasta
    // uma requisição nova.
    n?.querySelector('.shopinfo-shipping-inline__all')?.addEventListener('click', () => {
      calc.open();
      calc.showResults(full, true);
    });
  }

  // Falha NUNCA reexibe o último valor bom como atual. Sem cobertura não tem
  // "tentar de novo": repetir devolveria o mesmo nada — o caminho é o CEP.
  function fail(calc, kind) {
    // Mesma guarda de `loading`/`render` (ESTILO-4): continuação assíncrona
    // não pode presumir que o container sobreviveu ao morph do product-form.
    const t = target();
    if (!t) return;
    const d = t.dataset;
    const coverage = kind === 'coverage';
    const retry = coverage
      ? ''
      : '<button type="button" class="shopinfo-shipping-inline__retry">' + esc(d.msgRetry) + '</button>';
    const n = paint(
      '<div class="shopinfo-shipping-inline__error" role="alert">' + ALERT_ICON +
        '<span class="shopinfo-shipping-inline__error-text">' +
        esc(coverage ? d.msgNoCoverage : d.msgNetwork) +
        '</span>' + retry + '</div>' + footer(calc, d, ''),
      'error'
    );
    line();
    bindFooter(n, calc);
    n?.querySelector('.shopinfo-shipping-inline__retry')
      ?.addEventListener('click', () => quoteInline(calc, calc.savedCep));
  }

  /* ---------------------------------------------------------------------
   * Cotação
   * ------------------------------------------------------------------- */

  // Núcleo. Devolve `mine` junto com a promise para que quem chamou saiba, no
  // sucesso E no erro, se ainda era a cotação vigente quando ela voltou.
  // `lineOnly` (auto-cotacao, TASK-176): pinta SO a linha. O inline nasce
  // `hidden` abaixo dos botoes; abri-lo sem gesto empurraria a pagina (CLS) e
  // o buy box cresceria mais que os 36px da linha.
  function startQuote(calc, cep, lineOnly) {
    const mine = ++gen;
    // ESTILO-4: sem container não há config de cotação. Rejeitar (em vez de
    // estourar) mantém o contrato do retorno — quem chamou trata no `catch`.
    const t = target();
    if (!t) return { mine: mine, promise: Promise.reject(new Error('inline sem alvo')) };
    const d = t.dataset;
    abort = new AbortController();
    // TASK-179: so a vigente conta.
    const h = !!(SRC.cached && SRC.cached(d.variantId, cep));
    const res = (r, e) => mine === gen && ev('shipping_quote_result', { r: r, e: e, auto: lineOnly, cep: cep, hit: h });
    const promise = SRC.quote({
      endpoint: d.apiUrl,
      token: d.apiToken,
      variantId: d.variantId,
      cep: cep,
      country: d.countryCode,
      province: REG.ufFromCep(cep),
      signal: abort.signal,
    }).then((rates) => {
      res(rates);
      if (mine === gen && rates.length) lineOnly ? line(pick(rates)[0], calc) : render(calc, rates);
      return rates;
    }, (e) => {
      res(0, e);
      throw e;
    });
    return { mine: mine, promise: promise };
  }

  // Caminho só-inline: clique no CTA com CEP salvo, "tentar de novo" e
  // recotação por variante. Nunca roda no load. Aqui o inline é dono da
  // mensagem — é ele quem mostra cobertura e falha de rede.
  function quoteInline(calc, cep) {
    loading();
    const q = startQuote(calc, cep);
    q.promise.then(
      (rates) => {
        if (q.mine === gen && !rates.length) fail(calc, 'coverage');
      },
      (err) => {
        // Superada (abort da troca de variante já subiu a geração): quem
        // assumiu o inline é que manda.
        if (q.mine !== gen) return;
        console.error('[ShopinfoShipping]', err);
        fail(calc, err?.shopinfoKind === 'coverage' ? 'coverage' : 'network');
      }
    );
  }

  /* ---------------------------------------------------------------------
   * Auto-cotacao (TASK-176). Unica cotacao sem clique, e so da LINHA. Guardas:
   * linha na pagina, fonte de pe, loja BR, CEP salvo (cart.attributes ou
   * localStorage, via `calc.savedCep`) e teto por sessao. Dispara depois do
   * `load` E com a linha na viewport; uma tentativa por pageview. Qualquer
   * falha volta ao E1 (ou so ao link) em silencio: nenhum erro no load.
   * ------------------------------------------------------------------- */
  const AUTO_KEY = 'shopinfo_auto_quotes';
  const AUTO_MAX = 10;
  const AUTO_TIMEOUT = 20000;

  // Storage bloqueado conta como teto atingido: sem contador, sem auto.
  function autoCount(add) {
    try {
      const n = parseInt(sessionStorage.getItem(AUTO_KEY), 10) || 0;
      if (add) sessionStorage.setItem(AUTO_KEY, String(n + 1));
      return n;
    } catch (e) {
      return AUTO_MAX;
    }
  }

  function auto() {
    const l = document.querySelector('[data-shipping-line]');
    if (!l) return;
    // Mesmo gatilho do servidor (revisao TASK-176): sem o esqueleto impresso
    // pelo Liquid (cart.attributes), nada de cotar sozinho, mesmo com CEP no
    // localStorage: a linha nasceria em E1 e trocaria de texto sem gesto.
    if (!l.hasAttribute('data-saved-cep')) return line();
    const calc = REG.instance;
    const cep = calc && calc.savedCep;
    // Cache de sessao = sem rede: nao consome o teto (Correcao 1, TASK-176).
    const hit = () => !!(SRC.cached && SRC.cached(target()?.dataset.variantId, cep));
    const ok =
      cep && ready() && target().dataset.countryCode === 'BR' &&
      'IntersectionObserver' in window && (hit() || autoCount() < AUTO_MAX);
    if (!ok) return line();
    // JS assumiu: desliga a valvula CSS de 20s, senao ela revela o E1 antes da
    // linha entrar na viewport e o texto troca duas vezes. Toda saida e `line()`.
    l.classList.add('is-js');
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((x) => x.isIntersecting)) return;
      io.disconnect();
      // Clique, troca de variante ou CEP novo chegaram antes: quem assumiu manda.
      if (gen) return;
      if (!hit()) autoCount(true);
      const q = startQuote(calc, cep, true);
      // Timeout de cliente: unstable + retry + estavel passa de 20s. Abortar
      // rejeita a promise e cai no `line()` (E1/link).
      const ac = abort;
      const kill = setTimeout(() => ac && ac.abort(), AUTO_TIMEOUT);
      const done = () => clearTimeout(kill);
      q.promise.then(
        (rates) => { done(); if (q.mine === gen && !rates.length) line(); },
        () => { done(); if (q.mine === gen) line(); }
      );
    });
    io.observe(l);
  }

  // Recota SÓ quando já há cotação exibida — sem isso, trocar de variante
  // viraria cotação sem interação, que é justo o que "só por clique" proíbe.
  function onVariant(event) {
    const n = target();
    if (!n) return;
    // O evento borbulha de qualquer variant-picker da página (card, quick-add).
    // Só o da seção do produto aberto interessa.
    const scope = n.closest('.shopify-section');
    if (scope && !scope.contains(event.target)) return;

    const id = event?.detail?.resource?.id;
    // Trocar de variante invalida o que está em voo AINDA QUE nada esteja
    // exibido: sem isso a resposta da variante anterior volta, encontra a
    // geração intacta e pinta como se fosse a atual.
    const changed = !!id && String(id) !== n.dataset.variantId;
    if (id) n.dataset.variantId = String(id);
    if (event?.detail?.data?.newProduct) SRC.clearCache();

    const calc = REG.instance;
    // Recotar sozinho segue preso a `shown`: com o inline apagado, cotar aqui
    // seria cotação sem clique — justo o que "só por interação" proíbe.
    const recota = shown && calc && ready();
    if (!changed && !recota) return;

    clearTimeout(timer);
    // Aborta, invalida e troca de estado no mesmo tick: o rate da variante
    // anterior não sobrevive nem por um frame. A geração sobe aqui, e não só no
    // timer, porque uma resposta antiga pode chegar durante o debounce.
    abort?.abort();
    gen++;
    // Cotação em voo sem nada exibido (clique + troca de variante antes da
    // resposta) degrada para silêncio honesto, nunca para o valor da anterior.
    if (!recota) { clear(); return; }
    loading();
    timer = setTimeout(() => quoteInline(calc, calc.savedCep), DEBOUNCE);
  }

  // Registro. Só acontece com o global presente, a fonte carregada E o alvo no
  // documento: fora da PDP este arquivo nem é servido, e se um dia for, não faz
  // nada e não fala.
  if (REG?.registerQuoteProvider && SRC && target()) {
    document.addEventListener(VARIANT_EVENT, onVariant);
    REG.registerQuoteProvider({
      // O CTA da PDP nunca abre modal (Correção 1): com CEP salvo cota direto,
      // sem CEP salvo revela o form no lugar do CTA. `false` só quando este
      // clique não é da PDP (cep-box do header) ou a fonte não está de pé —
      // aí o modal é o caminho, como sempre foi.
      onOpen(btn, calc) {
        if (!btn.hasAttribute('data-shipping-quote-pdp') || !ready()) return false;
        if (calc.savedCep) quoteInline(calc, calc.savedCep);
        else openForm(calc, false);
        return true;
      },
      // Metade da conjunção que decide o modo do modal. `ready()` é exatamente
      // "posso cotar agora": fonte carregada, alvo no documento, token e
      // variante presentes. Não opina sobre QUEM clicou — isso é do modal.
      canQuote() {
        return ready();
      },

      // O CEP mudou pelo modal em MODO SALVAR. Reconciliação pelo estado atual
      // do inline — nunca cotação nova sem que alguém a tenha pedido:
      //
      //   result  — o que está na tela é do CEP antigo. Recota.
      //   error   — o rodapé imprime `calc.savedCep`, que JÁ é o CEP novo:
      //             deixar o erro antigo ali exibiria "não atende este CEP" ao
      //             lado do CEP recém-trocado. Recota, ou limpa se não dá.
      //   loading — cotação em voo para o CEP antigo. Não está na enumeração do
      //             plano, mas tem o mesmo defeito do `error`: deixada em paz,
      //             ela volta, encontra a geração intacta e pinta o rate do CEP
      //             velho sob o rodapé com o CEP novo.
      //   form    — o cliente já tem CEP; a pergunta foi respondida. Devolve o CTA.
      //   vazio   — inline apagado. Cotar aqui seria cotação sem pedido.
      onCepSaved(cep, calc) {
        const n = target();
        const state = n && n.dataset.state;
        // Inline apagado: a linha pode estar com a auto-cotacao do CEP antigo
        // (ou com ela em voo). Invalida e volta ao E1; cotar aqui seria sem pedido.
        if (!state) {
          gen++;
          line();
          return;
        }
        if (state === 'form') {
          clear();
          return;
        }
        if (state === 'result' || state === 'error' || state === 'loading') {
          if (ready()) quoteInline(calc, cep);
          else clear();
        }
      },

      // Chamado pelo modal. `null` devolve o turno ao cart-based; a promise
      // resolve com os rates do PRODUTO VISTO e, de quebra, pinta o inline.
      // Neste caminho o MODAL é dono da mensagem de erro: aqui o inline só não
      // pode continuar exibindo cotação velha.
      quote(cep, calc) {
        if (!ready()) return null;
        const q = startQuote(calc, cep);
        return q.promise.then(
          (rates) => {
            if (q.mine === gen && !rates.length) clear();
            return rates;
          },
          (err) => {
            if (q.mine === gen) clear();
            throw err;
          }
        );
      },
    });
    if (document.readyState === 'complete') auto();
    else window.addEventListener('load', auto, { once: true });
  } else {
    // Sem fonte ou sem alvo nao ha auto-cotacao: o fantasma nao pode ficar.
    line();
  }
})();
